import { datascopeTracker } from '../../utils/eventTracker';
import type { PresetKey } from './TaskAssignerFilters/useTaskAssignerDatePresets';

/** Values sent to Amplitude for `Applied Task Filter` / `filter_type`. */
export type AppliedTaskFilterType = 'location' | 'form' | 'status' | 'user' | 'task_id';

export function trackTaskAssignerViewList(accountId: number, tasksWithScheduledStatusCount: number) {
  datascopeTracker(
    'View Task Assignment List',
    { account_id: accountId, tasks_with_scheduled_status_count: tasksWithScheduledStatusCount },
    'AMP',
  );
}

export function trackTaskAssignerRemoveDateFilter(accountId: number) {
  datascopeTracker('Clicked Remove Date Filter', { account_id: accountId }, 'AMP');
}

export function trackTaskAssignerDatePreset(accountId: number, presetSelected: string) {
  datascopeTracker(
    'Clicked Date Preset',
    {
      account_id: accountId,
      preset_selected: presetSelected,
    },
    'AMP',
  );
}

export function trackTaskAssignerAppliedFilter(accountId: number, filterType: AppliedTaskFilterType) {
  datascopeTracker(
    'Applied Task Filter',
    {
      account_id: accountId,
      filter_type: filterType,
    },
    'AMP',
  );
}

export function trackTaskAssignerClearAllFilters(accountId: number) {
  datascopeTracker('Clicked Clear All Filters', { account_id: accountId }, 'AMP');
}

/**
 * Maps internal preset keys to Amplitude `preset_selected` (snake_case per analytics contract).
 * Used only when the user clicks a preset button; calendar-only ranges do not emit this event.
 * Keys not listed fall back to `more_options` (e.g. last 14/30/… days in the expanded panel).
 */
export function trackTaskCreated(params: {
  accountId: number;
  scheduledSendEnabled: boolean;
  scheduledSendOption: string;
  scheduledSendDaysBefore: number | null;
  userId?: number;
  participationType?: 'mandatory' | 'voluntary';
  hasReminders?: boolean;
  timeToCompleteMinutes?: number;
}) {
  datascopeTracker(
    'Task Created',
    {
      account_id: params.accountId,
      scheduled_send_enabled: params.scheduledSendEnabled,
      scheduled_send_option: params.scheduledSendOption,
      scheduled_send_days_before: params.scheduledSendDaysBefore,
      user_id: params.userId,
      participation_type: params.participationType,
      has_reminders: params.hasReminders,
      time_to_complete_minutes: params.timeToCompleteMinutes,
    },
    'AMP',
  );
}

// DS-3242 redesign analytics. Events listed in the ticket "Eventos Amplitude".
// Kept colocated with the rest of TaskAssigner tracking so consumers can find
// them by the same import path.

export function trackTaskCreateFormOpened(params: { accountId: number; userId?: number }) {
  datascopeTracker(
    'Task Create Form Opened',
    { account_id: params.accountId, user_id: params.userId },
    'AMP',
  );
}

export function trackTaskFormDrawerOpened(params: { accountId: number; userId?: number }) {
  datascopeTracker(
    'Task Form Drawer Opened',
    { account_id: params.accountId, user_id: params.userId },
    'AMP',
  );
}

export function trackTaskFormDrawerSelected(params: {
  accountId: number;
  userId?: number;
  formId: number;
}) {
  datascopeTracker(
    'Task Form Drawer Selected',
    { account_id: params.accountId, user_id: params.userId, form_id: params.formId },
    'AMP',
  );
}

export function trackTaskDatetimeConfigured(params: {
  accountId: number;
  userId?: number;
  durationHours: number;
  hasEndDate: boolean;
}) {
  datascopeTracker(
    'Task Datetime Configured',
    {
      account_id: params.accountId,
      user_id: params.userId,
      duration_hours: params.durationHours,
      has_end_date: params.hasEndDate,
    },
    'AMP',
  );
}

export function trackTaskEdited(params: {
  accountId: number;
  scheduledSendChanged: boolean;
  scheduledSendOptionBefore: string | null;
  scheduledSendOptionAfter: string;
}) {
  datascopeTracker(
    'Task Edited',
    {
      account_id: params.accountId,
      scheduled_send_changed: params.scheduledSendChanged,
      scheduled_send_option_before: params.scheduledSendOptionBefore,
      scheduled_send_option_after: params.scheduledSendOptionAfter,
    },
    'AMP',
  );
}

export function trackScheduledSendOptionSelected(params: {
  accountId: number;
  optionSelected: string;
  previousOption: string;
  daysBefore: number | null;
  context: 'create' | 'edit';
}) {
  datascopeTracker(
    'Scheduled Send Option Selected',
    {
      account_id: params.accountId,
      option_selected: params.optionSelected,
      previous_option: params.previousOption,
      days_before: params.daysBefore,
      context: params.context,
    },
    'AMP',
  );
}

export function trackTaskDetailViewed(params: {
  accountId: number;
  taskStatus: string;
  scheduledSendDaysBefore: number | null;
}) {
  datascopeTracker(
    'Task Detail Viewed',
    {
      account_id: params.accountId,
      task_status: params.taskStatus,
      scheduled_send_days_before: params.scheduledSendDaysBefore,
    },
    'AMP',
  );
}

/** Maps a scheduledSendDaysBefore value to an Amplitude option string. */
export function mapScheduledSendToOption(daysBefore: number | null | undefined): string {
  if (daysBefore === null || daysBefore === undefined) return 'send_now';
  if (daysBefore === 0) return 'same_day';
  return `${daysBefore}_days_before`;
}

export function mapPresetKeyToAmplitudePreset(presetKey: PresetKey): string {
  const map: Partial<Record<PresetKey, string>> = {
    today: 'today',
    thisWeek: 'this_week',
    nextWeek: 'next_week',
    last7Days: 'last_7_days',
    next7Days: 'next_7_days',
  };
  return map[presetKey] ?? 'more_options';
}
